import type React from 'react';
import { useCallback, useContext, useMemo, type JSX } from 'react';
import { ChatSessionMessageQueriesContext } from '@trinity/chat-components/components/Chat/context';
import {
  useAudioPlayerScan,
  useAudioPlayerState,
  useToggleAudioPlaying,
} from '@trinity/chat-components/containers/AudioPlayer/index';
import {
  useInitiateQuerySpeechToTextNullSafe,
  useStopTextToSpeechContextNullSafe,
  useTextToSpeechStateContextNullSafe,
} from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context';

interface AudioPlayerControllerProps {
  children: (props: {
    duration: number;
    playing: boolean;
    isLoading: boolean;
    position: number;
    onSeek: (value: number) => void;
    onScan: (value: number) => void;
    onStop: () => void;
    onPreviousTrack: () => void;
    onNextTrack: () => void;
    onSkipBackward: () => void;
    onSkipAhead: () => void;
    onTogglePlay: () => void;
    canSkipBackward: boolean;
    canSkipForward: boolean;
  }) => JSX.Element;
}

const AudioPlayerController: React.FC<AudioPlayerControllerProps> = ({ children }) => {
  const stopTextToSpeech = useStopTextToSpeechContextNullSafe();
  const initiateReadAloud = useInitiateQuerySpeechToTextNullSafe();
  const ttsState = useTextToSpeechStateContextNullSafe();

  const [{ duration, playing }] = useAudioPlayerState();
  const togglePlaying = useToggleAudioPlaying();
  const { scan, seek, position } = useAudioPlayerScan();
  const onStop = useCallback(() => {
    stopTextToSpeech();
  }, [stopTextToSpeech]);

  const queries = useContext(ChatSessionMessageQueriesContext);

  const currentIndex = useMemo(() => {
    return queries.findIndex((query) => !!ttsState.queryId && query.id === ttsState.queryId);
  }, [queries, ttsState]);

  const canSkipBackward = useMemo(() => currentIndex > 0, [currentIndex]);
  const canSkipForward = useMemo(
    () => currentIndex >= 0 && currentIndex < queries.length - 1,
    [currentIndex, queries]
  );

  const onPreviousTrack = useCallback(() => {
    stopTextToSpeech(true);
    if (currentIndex === -1 || currentIndex === 0) {
      return;
    }
    const targetQuery = queries[currentIndex - 1];

    if (!targetQuery) return;

    initiateReadAloud({ queryId: targetQuery.id });
  }, [currentIndex, queries, stopTextToSpeech, initiateReadAloud]);

  const onNextTrack = useCallback(() => {
    stopTextToSpeech(true);
    if (currentIndex === -1 || currentIndex >= queries.length - 1) {
      return;
    }
    const targetQuery = queries[currentIndex + 1];
    if (!targetQuery) return;
    initiateReadAloud({ queryId: targetQuery.id });
  }, [currentIndex, queries, stopTextToSpeech, initiateReadAloud]);

  const onSkipBackward = useCallback(() => {
    const newTime = Math.max(position - 15, 0);
    seek(newTime);
  }, [position, seek]);

  const onSkipAhead = useCallback(() => {
    const newTime = Math.min(position + 15, duration || Infinity);
    seek(newTime);
  }, [position, duration, seek]);

  const isLoading = ttsState.state === 'loading';

  return children({
    duration: duration || 0,
    position,
    isLoading,
    onStop,
    onPreviousTrack,
    onNextTrack,
    onScan: scan,
    onSeek: seek,
    onSkipBackward,
    onSkipAhead,
    playing,
    onTogglePlay: togglePlaying,
    canSkipBackward,
    canSkipForward,
  });
};

export default AudioPlayerController;
